/* @jsx h */
import { defineComponent, ref, computed, h, onMounted, provide } from "vue";
import { type FloorPlan } from "@lemmonhq/types";
import { Loading } from "@app/vue/components/index";
import { setTitle } from "@lemmonhq/orders";
import { useShop, useAuth } from "../../providers";
import { TableQRModal } from "./TableQRModal";
import { TableReservationsPopup } from "./TableReservationsPopup";
import { useNow } from "../../composables/useNow";
import { ReservationDetails } from "../ManagerReservations/subpages/ReservationsSchedule/components/ReservationDetails";
import { AcceptReservationRequest } from "../ManagerReservations/subpages/ReservationTabs/AcceptReservationRequest";
import { isConfirmedStatus } from "../../utils/reservationStatus";
import { type FloorPlanViewMode, type DropModeState } from "./tables-types";
import { TablesHeader } from "./components/TablesHeader";
import { TablesBody } from "./components/TablesBody";
import { useFloorPlanEditor } from "./composables/useFloorPlanEditor";
import { useTableFilters } from "./composables/useTableFilters";
import { useTableReservationMode } from "./composables/useTableReservationMode";
import {
  useReservationDropMode,
  emptyDrop,
} from "./composables/useReservationDropMode";

export type { FloorPlanViewMode, DropModeState } from "./tables-types";

export { TablesHeader } from "./components/TablesHeader";
export { TablesBody } from "./components/TablesBody";
export { TablesViewSwitcher } from "./components/TablesViewSwitcher";

export const TablesPage = defineComponent({
  name: "TablesPage",
  setup() {
    const shop = useShop();
    const auth = useAuth();
    const { tables, floorPlans } = shop;
    const loading = ref(true);
    const isMobile = window.matchMedia("(max-width: 639px)").matches;
    const lsCurrentView = isMobile
      ? "list"
      : localStorage.getItem("tables_default_view") || "list";
    const currentView = ref<"floor_plan" | "list">(lsCurrentView as any);
    const floorPlanViewMode = ref<FloorPlanViewMode>(isMobile ? "all" : "all");

    const nowMs = useNow();

    const restaurantTz = computed(
      () => auth.user.value?.restaurant?.timezone || "UTC",
    );

    provide("timezone", restaurantTz);

    // ── Composables
    const {
      activeFloorPlan,
      currentFloorPlan,
      isDirty,
      handleFloorPlanChange,
      handleSaveFloorPlan,
      handleResetFloorPlan,
      handleFloorPlanSelect,
    } = useFloorPlanEditor(floorPlans);

    const reservationMode = useTableReservationMode(restaurantTz);
    const {
      selectedDate,
      isSelectedToday,
      showReservationSidebar,
      reservationSidebarTab,
      tableReservationFilter,
      displayedReservations,
      isLoadingReservations,
      loadReservationsForDate,
      refreshTablesReservations,
      enterReservationMode: enterResModeBase,
      openReservationList,
      closeReservationSidebar,
      exitReservationMode: exitResModeBase,
      reservationCounts,
    } = reservationMode;

    const dropMode = useReservationDropMode(
      tables,
      floorPlans,
      displayedReservations,
      isSelectedToday,
      refreshTablesReservations,
      shop.refreshData,
      shop.refreshTables,
      handleFloorPlanSelect,
      currentView,
      floorPlanViewMode,
    );
    const {
      dropModeState,
      viewingReservation,
      viewingRequest,
      loadingReservationCollections,
      handleEnterDropMode,
      handleEnterDropModeForRequest,
      handleExitDropMode,
      handleSelectReservation,
      handleSelectRequest,
      handleShowReservationDetail,
      handleShowRequestDetail,
      handleChangeTables,
      handleTableClickInDropMode: handleTableClickRaw,
      handleConfirmDropMode,
    } = dropMode;

    const { searchQuery, filter, filteredTables } = useTableFilters(
      tables,
      floorPlans,
      activeFloorPlan,
      floorPlanViewMode,
    );

    // ── Floor live-day flag
    const floorIsLiveDay = computed(
      () => !dropModeState.value.active || isSelectedToday.value,
    );

    // ── Loading composite
    const loadingCollections = computed(
      () => isLoadingReservations.value || loadingReservationCollections.value,
    );

    // ── Mode transitions (wire sidebar close into select handlers)
    const enterReservationMode = () => {
      dropModeState.value = emptyDrop(true);
      enterResModeBase();
    };

    const exitReservationMode = () => {
      dropModeState.value = emptyDrop(false);
      exitResModeBase();
    };

    // handleTableClickInDropMode may return a tableReservationFilter update
    const handleTableClickInDropMode = (tableId: number) => {
      const result = handleTableClickRaw(tableId);
      if (result && "tableId" in result) {
        tableReservationFilter.value = result as {
          tableId: number;
          tableName: string;
        };
        showReservationSidebar.value = true;
      }
    };

    const tableQRModalOpen = ref<number>();

    onMounted(async () => {
      setTitle("Tables");
      try {
        await shop.refreshData();
      } finally {
        loading.value = false;
      }
      loadReservationsForDate();
      if (
        new URLSearchParams(window.location.search).get("mode") ===
        "reservations"
      ) {
        currentView.value = "floor_plan";
        enterReservationMode();
      }
    });

    return () => {
      return loading.value ? (
        <div class="w-full h-full flex-1 flex justify-center items-center">
          <Loading />
        </div>
      ) : (
        <div class="flex flex-1 flex-col overflow-y-hidden">
          <TablesHeader
            currentView={currentView}
            activeFloorPlan={activeFloorPlan}
            searchQuery={searchQuery}
            floorPlans={floorPlans}
            tables={tables}
            filter={filter}
            isDirty={isDirty}
            onSave={handleSaveFloorPlan}
            onReset={handleResetFloorPlan}
            onFloorPlanSelect={handleFloorPlanSelect}
            onFloorPlanCreated={shop.refreshData}
            fetchTables={shop.refreshData}
            isLoading={loading}
            floorPlanViewMode={floorPlanViewMode}
            dropModeState={dropModeState}
            onEnterDropMode={handleEnterDropMode}
            onExitDropMode={handleExitDropMode}
            onConfirmDropMode={handleConfirmDropMode}
            reservations={displayedReservations}
            confirmedCount={reservationCounts.confirmed.value}
            pendingCount={reservationCounts.pending.value}
            seatedCount={reservationCounts.seated.value}
            selectedDate={selectedDate}
            onDateChange={(d: Date) => (selectedDate.value = d)}
            isSelectedToday={isSelectedToday}
            onEnterReservationMode={enterReservationMode}
            onExitReservationMode={exitReservationMode}
            onOpenReservationList={openReservationList}
            onShowReservationDetail={handleShowReservationDetail}
            onShowRequestDetail={handleShowRequestDetail}
          />
          <TablesBody
            currentView={currentView}
            filteredTables={filteredTables}
            floorPlans={floorPlans}
            tables={tables}
            activeFloorPlan={activeFloorPlan}
            currentFloorPlan={currentFloorPlan}
            onFloorPlanChange={handleFloorPlanChange}
            fetchTables={shop.refreshData}
            isLoading={loading}
            onShowQR={(tableId) => (tableQRModalOpen.value = tableId)}
            floorPlanViewMode={floorPlanViewMode}
            dropModeState={dropModeState}
            onTableClickInDropMode={handleTableClickInDropMode}
            reservations={displayedReservations}
            isLiveDay={floorIsLiveDay}
            nowMs={nowMs.value}
          />
          {tableQRModalOpen.value ? (
            <TableQRModal
              tables={tables.value}
              tableId={tableQRModalOpen.value}
              onClose={() => (tableQRModalOpen.value = undefined)}
            />
          ) : null}
          <TableReservationsPopup
            isOpen={showReservationSidebar.value}
            activeTab={reservationSidebarTab.value}
            tableFilter={tableReservationFilter.value}
            selectedDate={selectedDate.value}
            isToday={isSelectedToday.value}
            nowMs={nowMs.value}
            onClose={closeReservationSidebar}
            onSelectReservation={(r) =>
              handleSelectReservation(r, closeReservationSidebar)
            }
            onSelectRequest={(req) =>
              handleSelectRequest(req, closeReservationSidebar)
            }
            onViewReservation={handleShowReservationDetail}
            reservations={displayedReservations.value}
            pendingRequests={reservationMode.pendingRequests.value}
            isLoadingReservations={loadingCollections.value}
          />
          {viewingReservation.value && (
            <ReservationDetails
              key={viewingReservation.value.id}
              reservation={viewingReservation.value}
              onClose={() => (viewingReservation.value = null)}
              onUpdate={(updated: any) => {
                shop.refreshData();
                loadReservationsForDate();
                if (
                  updated &&
                  !isConfirmedStatus(updated.status) &&
                  dropModeState.value.reservation?.id === updated.id
                ) {
                  handleExitDropMode();
                }
              }}
              onReservationCancel={() => {
                shop.refreshData();
                loadReservationsForDate();
                if (dropModeState.value.active) handleExitDropMode();
                viewingReservation.value = null;
              }}
              onChangeTables={handleChangeTables}
            />
          )}
          {viewingRequest.value && (
            <AcceptReservationRequest
              key={viewingRequest.value.id}
              request={viewingRequest.value}
              isOpen={true}
              mapMode={true}
              mapSelectedTables={dropModeState.value.selectedTableIds.map(
                (id) => ({
                  id,
                  name:
                    tables.value.find(
                      (tb) => Number(tb.table.id) === Number(id),
                    )?.table.name || `#${id}`,
                }),
              )}
              onAssignOnMap={() => {
                const req = viewingRequest.value!;
                viewingRequest.value = null;
                if (dropModeState.value.request?.id !== req.id)
                  handleEnterDropModeForRequest(req);
              }}
              onClose={() => (viewingRequest.value = null)}
              handleActionComplete={() => {
                viewingRequest.value = null;
                if (dropModeState.value.request) handleExitDropMode();
                shop.refreshTables();
                shop.refreshData();
                loadReservationsForDate();
              }}
            />
          )}
        </div>
      );
    };
  },
});
